<script lang="ts" setup>
import { ref, nextTick } from 'vue';
import { QrcodeStream } from 'vue-qrcode-reader';
import { evaluateQrDetection, type CornerPoint, type DetectedQrCode } from '@/features/baggageQrScan/model/qrDetection';
import ScanFrame from './ScanFrame.vue';
import PreviewChip from './PreviewChip.vue';

const props = defineProps<{
    detectedQrCode?: string | null;
    scannedItems?: readonly string[];
}>();

const emit = defineEmits<{
    detect: [qrCode: string];
    confirm: [];
    cancel: [];
}>();

const isLoading = ref(true);
const cameraError = ref<string | null>(null);
const cameraViewRef = ref<HTMLElement | null>(null);
const paused = ref(false);

// MEMO: vue-qrcode-reader v5 は内部キャッシュにより同一QRコードの連続検出で @detect が発火しない。
// カメラを一瞬 pause → resume することでキャッシュをクリアし、同一コードの再検出を可能にする。
// ref: https://github.com/gruhn/vue-qrcode-reader/issues/227
const resetCamera = () => {
    paused.value = true;
    nextTick(() => {
        paused.value = false;
    });
};

const SCAN_FRAME_SIZE = 280;

const onCameraReady = () => {
    isLoading.value = false;
};

const onCameraError = (error: Error) => {
    isLoading.value = false;
    if (error.name === 'NotAllowedError') {
        cameraError.value = 'カメラへのアクセスが許可されていません';
    } else if (error.name === 'NotFoundError') {
        cameraError.value = 'カメラが見つかりません';
    } else {
        cameraError.value = `カメラエラー: ${error.message}`;
    }
};

const drawBoundingBox = (cornerPoints: CornerPoint[], ctx: CanvasRenderingContext2D) => {
    const [firstPoint, ...otherPoints] = cornerPoints;
    if (!firstPoint) return;

    ctx.strokeStyle = '#ff0000';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(firstPoint.x, firstPoint.y);
    for (const { x, y } of otherPoints) {
        ctx.lineTo(x, y);
    }
    ctx.lineTo(firstPoint.x, firstPoint.y);
    ctx.closePath();
    ctx.stroke();
};

const paintBoundingBox = (detectedCodes: DetectedQrCode[], ctx: CanvasRenderingContext2D) => {
    if (!cameraViewRef.value) return;

    const { currentCodeOutOfFrame, scannableCodes } = evaluateQrDetection(
        detectedCodes,
        props.detectedQrCode,
        props.scannedItems ?? [],
        cameraViewRef.value.clientWidth,
        cameraViewRef.value.clientHeight,
        SCAN_FRAME_SIZE
    );

    if (currentCodeOutOfFrame) {
        emit('cancel');
    }

    const firstCode = scannableCodes[0];
    if (firstCode) {
        emit('detect', firstCode.rawValue);
    }

    for (const code of scannableCodes) {
        drawBoundingBox(code.cornerPoints, ctx);
    }
};
</script>

<template>
    <div ref="cameraViewRef" class="camera-view">
        <div v-if="isLoading && !cameraError" class="loading">
            <v-progress-circular indeterminate color="white" />
            <p>カメラを起動中...</p>
        </div>

        <div v-else-if="cameraError" class="error">
            <v-icon size="48" color="error">mdi-camera-off</v-icon>
            <p>{{ cameraError }}</p>
        </div>

        <QrcodeStream
            v-show="!isLoading && !cameraError"
            class="qr-stream"
            :paused="paused"
            :constraints="{ facingMode: 'environment' }"
            :track="paintBoundingBox"
            @camera-on="onCameraReady"
            @error="onCameraError"
        />

        <ScanFrame v-if="!isLoading && !cameraError" :detected="!!detectedQrCode" />

        <PreviewChip
            v-if="detectedQrCode"
            :qr-code="detectedQrCode"
            @confirm="
                () => {
                    resetCamera();
                    emit('confirm');
                }
            "
        />
    </div>
</template>

<style scoped>
.camera-view {
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #000;
    overflow: hidden;
    border-radius: 8px;

    & .loading,
    & .error {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        color: #fff;
    }

    & .qr-stream {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}
</style>
